import React, { useState, useEffect } from 'react';
import { supabase } from '../constants';
import { probVictoire, getPlayerComparison } from '../utils';
import WinGauge from './WinGauge';

function UpcomingMatches({ onAnalyzeMatch }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupedMatches, setGroupedMatches] = useState({});
  const [selectedSurface, setSelectedSurface] = useState('Hard');
  const [matchProbabilities, setMatchProbabilities] = useState({});
  const [calculatingProbabilities, setCalculatingProbabilities] = useState(false);
  const [analyzingMatch, setAnalyzingMatch] = useState(null);

  
  useEffect(() => {
    async function fetchMatches() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('upcoming_matches')
          .select('*')
          .order('date')
          .order('heure');

        if (error) {
          throw error;
        }

        setMatches(data);
        
        // Regrouper les matchs par date et tournoi
        const grouped = data.reduce((acc, match) => {
          const dateKey = match.date;
          if (!acc[dateKey]) {
            acc[dateKey] = {};
          }
          
          const tournoi = match.tournoi || 'Non spécifié';
          if (!acc[dateKey][tournoi]) {
            acc[dateKey][tournoi] = [];
          }
          
          acc[dateKey][tournoi].push(match);
          return acc;
        }, {});
        
        setGroupedMatches(grouped);
      } catch (error) {
        console.error('Erreur lors de la récupération des matchs:', error);
        setError("Impossible de charger les matchs à venir. Veuillez réessayer plus tard.");
      } finally {
        setLoading(false);
      }
    }

    fetchMatches();
  }, []);
  
  // Calcul des probabilités lorsque la surface change ou les matchs sont chargés
  useEffect(() => {
    async function calculateAllProbabilities() {
      if (!matches.length) return;
      
      setCalculatingProbabilities(true);
      console.log(`Calcul des probabilités pour la surface: ${selectedSurface}`);
      
      const newProbabilities = {};
      
      try {
        // Traiter les matchs par lots pour éviter de surcharger le navigateur
        const batchSize = 5;
        for (let i = 0; i < matches.length; i += batchSize) {
          const batch = matches.slice(i, i + batchSize);
          
          await Promise.all(batch.map(async (match) => {
            try {
              // Extraire les noms de famille pour une recherche plus flexible
              const player1LastName = match.player1.split(' ').pop();
              const player2LastName = match.player2.split(' ').pop();
              
              console.log(`Recherche pour: ${match.player1} (${player1LastName}) vs ${match.player2} (${player2LastName})`);
              
              // Récupérer les données des joueurs par nom de famille
              const [player1Response, player2Response] = await Promise.all([
                supabase
                  .from('atp_elo_ratings')
                  .select('*')
                  .ilike('player', `%${player1LastName}%`)
                  .order('elo', { ascending: false }),
                supabase
                  .from('atp_elo_ratings')
                  .select('*')
                  .ilike('player', `%${player2LastName}%`)
                  .order('elo', { ascending: false })
              ]);
              
              console.log(`Résultats pour ${player1LastName}:`, player1Response.data?.length || 0);
              console.log(`Résultats pour ${player2LastName}:`, player2Response.data?.length || 0);
              
              // Vérifier si les deux joueurs sont trouvés
              if (player1Response.data?.length > 0 && player2Response.data?.length > 0) {
                const player1 = player1Response.data[0];
                const player2 = player2Response.data[0];
                
                console.log(`Joueurs trouvés: ${player1.player} (${player1.player_slug}) vs ${player2.player} (${player2.player_slug})`);
                
                try {
                  // Utiliser le calcul avancé pour les probabilités (même que dans l'analyse détaillée)
                  const comparisonResult = await getPlayerComparison(player1, player2, selectedSurface);
                  console.log('Résultat de comparaison avancée:', comparisonResult);
                  
                  // Utiliser les résultats avancés
                  newProbabilities[match.id] = {
                    player1: {
                      name: player1.player,
                      probability: comparisonResult.advanced.player1.toFixed(1)
                    },
                    player2: {
                      name: player2.player,
                      probability: comparisonResult.advanced.player2.toFixed(1)
                    },
                    calculated: true,
                    surface: selectedSurface,
                    matchedNames: {
                      player1: { original: match.player1, matched: player1.player },
                      player2: { original: match.player2, matched: player2.player }
                    },
                    // Stocker les données complètes pour référence
                    fullComparison: comparisonResult
                  };
                  
                  console.log(`Probabilités avancées calculées pour match ${match.id}:`, 
                             `${player1.player}: ${comparisonResult.advanced.player1}%`, 
                             `${player2.player}: ${comparisonResult.advanced.player2}%`);
                } catch (error) {
                  console.error(`Erreur lors du calcul avancé pour le match ${match.id}:`, error);
                  
                  // Repli sur le calcul simple en cas d'erreur
                  const p1Win = probVictoire(player1, player2, selectedSurface);
                  const p2Win = 1 - p1Win;
                  
                  newProbabilities[match.id] = {
                    player1: {
                      name: player1.player,
                      probability: (p1Win * 100).toFixed(1)
                    },
                    player2: {
                      name: player2.player,
                      probability: (p2Win * 100).toFixed(1)
                    },
                    calculated: true,
                    surface: selectedSurface,
                    matchedNames: {
                      player1: { original: match.player1, matched: player1.player },
                      player2: { original: match.player2, matched: player2.player }
                    },
                    isSimpleCalc: true
                  };
                  
                  console.log(`Probabilités simples (fallback) calculées pour match ${match.id}:`, 
                             `${player1.player}: ${(p1Win * 100).toFixed(1)}%`, 
                             `${player2.player}: ${(p2Win * 100).toFixed(1)}%`);
                }
              } else {
                newProbabilities[match.id] = {
                  calculated: false,
                  reason: 'Un ou plusieurs joueurs non trouvés',
                  details: {
                    player1Found: player1Response.data?.length > 0,
                    player2Found: player2Response.data?.length > 0,
                    player1Results: player1Response.data?.length || 0,
                    player2Results: player2Response.data?.length || 0
                  }
                };
                console.log(`Joueurs non trouvés pour le match ${match.id}: ${match.player1} vs ${match.player2}`);
              }
            } catch (error) {
              console.error(`Erreur lors du calcul des probabilités pour le match ${match.id}:`, error);
              newProbabilities[match.id] = {
                calculated: false,
                reason: 'Erreur lors du calcul',
                error: error.message
              };
            }
          }));
        }
        
        console.log('Toutes les probabilités calculées:', newProbabilities);
        // Vérifier combien de matchs ont des probabilités calculées
        const calculatedCount = Object.values(newProbabilities).filter(p => p.calculated).length;
        console.log(`${calculatedCount}/${Object.keys(newProbabilities).length} matchs avec probabilités calculées`);
        
        setMatchProbabilities(newProbabilities);
      } catch (error) {
        console.error('Erreur lors du calcul des probabilités:', error);
      } finally {
        setCalculatingProbabilities(false);
      }
    }
    
    calculateAllProbabilities();
  }, [matches, selectedSurface]);

  const handleAnalyzeClick = async (match) => {
    try {
      console.log('Match à analyser:', match);
      
      // Set loading state for this specific match
      setAnalyzingMatch(match.id);
      
      // On voit que les URLs sont au format "player.cgi", donc nous allons faire une recherche directe par nom
      console.log(`Recherche directe des joueurs par nom: "${match.player1}" et "${match.player2}"`);
      
      // Récupérer les données des joueurs directement par nom
      let [player1Response, player2Response] = await Promise.all([
        supabase
          .from('atp_elo_ratings')
          .select('*')
          .ilike('player', `%${match.player1}%`)
          .order('elo', { ascending: false }),
        supabase
          .from('atp_elo_ratings')
          .select('*')
          .ilike('player', `%${match.player2}%`)
          .order('elo', { ascending: false })
      ]);
      
      console.log('Réponses détaillées de recherche par nom:');
      console.log('Joueur 1:', player1Response);
      console.log('Joueur 2:', player2Response);
      
      // Vérifier les erreurs
      if (player1Response.error) {
        throw new Error(`Erreur pour ${match.player1}: ${player1Response.error.message}`);
      }
      
      if (player2Response.error) {
        throw new Error(`Erreur pour ${match.player2}: ${player2Response.error.message}`);
      }
      
      // Vérifier si des joueurs ont été trouvés
      if (!player1Response.data || player1Response.data.length === 0) {
        // Essayer une recherche par nom de famille
        const lastName = match.player1.split(' ').pop();
        const fallbackSearch = await supabase
          .from('atp_elo_ratings')
          .select('*')
          .ilike('player', `%${lastName}%`)
          .order('elo', { ascending: false });
          
        console.log(`Recherche par nom de famille pour ${match.player1} (${lastName}):`, fallbackSearch);
        
        if (fallbackSearch.data && fallbackSearch.data.length > 0) {
          // Utiliser le premier résultat de la recherche par nom de famille
          player1Response = fallbackSearch;
          console.log(`Utilisation du résultat de recherche par nom de famille pour ${match.player1}`);
        } else {
          throw new Error(`Joueur "${match.player1}" non trouvé dans la base de données`);
        }
      }
      
      if (!player2Response.data || player2Response.data.length === 0) {
        // Essayer une recherche par nom de famille
        const lastName = match.player2.split(' ').pop();
        const fallbackSearch = await supabase
          .from('atp_elo_ratings')
          .select('*')
          .ilike('player', `%${lastName}%`)
          .order('elo', { ascending: false });
          
        console.log(`Recherche par nom de famille pour ${match.player2} (${lastName}):`, fallbackSearch);
        
        if (fallbackSearch.data && fallbackSearch.data.length > 0) {
          // Utiliser le premier résultat de la recherche par nom de famille
          player2Response = fallbackSearch;
          console.log(`Utilisation du résultat de recherche par nom de famille pour ${match.player2}`);
        } else {
          throw new Error(`Joueur "${match.player2}" non trouvé dans la base de données`);
        }
      }
      
      // Prendre le premier joueur si plusieurs correspondent
      const player1Data = player1Response.data[0];
      const player2Data = player2Response.data[0];
      
      console.log(`Joueurs trouvés: ${player1Data.player} (${player1Data.player_slug}) et ${player2Data.player} (${player2Data.player_slug})`);
      
      if (player1Data && player2Data) {
        console.log('Lancement de l\'analyse avec les joueurs trouvés');
        onAnalyzeMatch(player1Data, player2Data);
      } else {
        alert('Données de joueur manquantes ou incomplètes');
      }
    } catch (error) {
      console.error('Erreur lors de l\'analyse du match:', error);
      alert(`Impossible d'analyser ce match: ${error.message}`);
    } finally {
      // Clear loading state
      setAnalyzingMatch(null);
    }
  };

  // Fonction alternative si les URLs ne sont pas disponibles ou ne fonctionnent pas
  // eslint-disable-next-line no-unused-vars
  const fetchPlayersBySlug = async (player1Slug, player2Slug) => {
    try {
      const [player1Response, player2Response] = await Promise.all([
        supabase.from('atp_elo_ratings').select('*').eq('player_slug', player1Slug),
        supabase.from('atp_elo_ratings').select('*').eq('player_slug', player2Slug)
      ]);
      
      // Vérifier les erreurs de réponse
      if (player1Response.error || player2Response.error) {
        throw new Error('Erreur lors de la récupération des données des joueurs');
      }
      
      // Vérifier si des joueurs ont été trouvés
      if (!player1Response.data || player1Response.data.length === 0) {
        throw new Error(`Joueur avec slug "${player1Slug}" non trouvé`);
      }
      
      if (!player2Response.data || player2Response.data.length === 0) {
        throw new Error(`Joueur avec slug "${player2Slug}" non trouvé`);
      }
      
      return {
        player1: player1Response.data[0], // Prendre le premier joueur si plusieurs correspondent
        player2: player2Response.data[0]
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des joueurs par slug:', error);
      throw error;
    }
  };

  const isMatchAnalyzable = (match) => {
    return match.player1 && match.player2 && 
           match.player1.trim() !== '' && match.player2.trim() !== '';
  };
  
  const handleSurfaceChange = (e) => {
    const newSurface = e.target.value;
    console.log(`Changement de surface: ${selectedSurface} -> ${newSurface}`);
    
    // Effacer d'abord les probabilités actuelles
    setMatchProbabilities({});
    
    // Mettre à jour la surface sélectionnée
    setSelectedSurface(newSurface);
    
    // Indiquer que les calculs sont en cours
    setCalculatingProbabilities(true);
  };



  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Chargement des matchs à venir...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#d9534f' }}>
        <p>{error}</p>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Aucun match à venir n'est disponible pour le moment.</p>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: "20px auto", 
      fontFamily: "Arial, sans-serif",
      background: "#fff", 
      padding: 32, 
      borderRadius: 16, 
      boxShadow: "0 0 24px #ddd"
    }}>
      <h2 style={{ textAlign: "center", marginBottom: 20 }}>Matchs à venir</h2>
      
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <p style={{ margin: 0 }}><strong>Note:</strong> Cliquez sur "Analyser" pour voir plus de détails sur un match.</p>
        
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <label htmlFor="surface-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>Surface:</label>
          <select 
            id="surface-select"
            value={selectedSurface}
            onChange={handleSurfaceChange}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              backgroundColor: '#fff'
            }}
          >
            <option value="Hard">Dur</option>
            <option value="Clay">Terre battue</option>
            <option value="Grass">Gazon</option>
          </select>
        </div>
      </div>
      
      {calculatingProbabilities && (
        <div style={{ 
          textAlign: 'center', 
          padding: '15px', 
          backgroundColor: '#e8f7ff', 
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '15px'
        }}>
          <div className="loading-spinner"></div>
          <p style={{ margin: 0, fontWeight: 'bold' }}>
            Calcul des probabilités pour <span style={{ color: '#3498db' }}>
              {selectedSurface === 'Hard' ? 'Dur' : selectedSurface === 'Clay' ? 'Terre battue' : 'Gazon'}
            </span>...
          </p>
        </div>
      )}
      
      {Object.entries(groupedMatches).map(([date, tournaments]) => (
        <div key={date} style={{ marginBottom: 30 }}>
          <h3 style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '10px 15px', 
            borderRadius: '8px',
            marginBottom: '15px',
            color: '#333'
          }}>
            {date === 'Demain' ? 'Demain' : date}
          </h3>
          
          {Object.entries(tournaments).map(([tournoi, matchesList]) => (
            <div key={`${date}-${tournoi}`} style={{ marginBottom: 20 }}>
              <h4 style={{ 
                color: '#3498db', 
                borderBottom: '1px solid #eee', 
                paddingBottom: '8px', 
                marginBottom: '10px' 
              }}>
                {tournoi}
              </h4>
              
              <div>
                {matchesList.map(match => (
                  <div key={match.id} style={{ 
                    padding: '12px', 
                    marginBottom: '8px', 
                    backgroundColor: '#f9f9f9', 
                    borderRadius: '8px',
                    border: '1px solid #eee',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold' }}>{match.heure}</span>
                      {match.tour && (
                        <span style={{ 
                          backgroundColor: '#e8f4f8', 
                          padding: '3px 8px', 
                          borderRadius: '4px', 
                          fontSize: '12px' 
                        }}>
                          {match.tour}
                        </span>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '5px 0' }}>
                      <div style={{ 
                        flex: 1, 
                        textAlign: 'right', 
                        padding: '0 10px',
                        color: isMatchAnalyzable(match) ? 'inherit' : '#d9534f'
                      }}>
                        <div style={{ fontWeight: matchProbabilities[match.id]?.player1?.probability > 50 ? 'bold' : 'normal' }}>
                          {match.player1}
                        </div>
                        
                        {matchProbabilities[match.id]?.calculated ? (
                          <div style={{ 
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '5px'
                          }}>
                            {matchProbabilities[match.id].isSimpleCalc && (
                              <div style={{ fontSize: '9px', color: '#f0ad4e', textAlign: 'right' }}>
                                calcul simple
                              </div>
                            )}
                            <WinGauge 
                              value={parseFloat(matchProbabilities[match.id].player1.probability)} 
                              size={60}
                              primary={parseFloat(matchProbabilities[match.id].player1.probability) > 50 ? '#5cb85c' : '#c74a4a'}
                            />
                          </div>
                        ) : (
                          <div style={{ 
                            marginTop: '5px', 
                            fontSize: '11px', 
                            color: '#d9534f', 
                            fontStyle: 'italic',
                            textAlign: 'right'
                          }}>
                            Joueur non trouvé
                          </div>
                        )}
                      </div>
                      
                      <div style={{ 
                        fontWeight: 'bold', 
                        padding: '0 15px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <span>VS</span>
                        {matchProbabilities[match.id]?.calculated && (
                          <div style={{ 
                            fontSize: '10px', 
                            color: '#777',
                            marginTop: '5px',
                            backgroundColor: '#f0f0f0',
                            padding: '2px 5px',
                            borderRadius: '4px'
                          }}>
                            {selectedSurface === 'Hard' ? 'Dur' : 
                             selectedSurface === 'Clay' ? 'Terre' : 'Gazon'}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ 
                        flex: 1, 
                        textAlign: 'left', 
                        padding: '0 10px',
                        color: isMatchAnalyzable(match) ? 'inherit' : '#d9534f'
                      }}>
                        <div style={{ fontWeight: matchProbabilities[match.id]?.player2?.probability > 50 ? 'bold' : 'normal' }}>
                          {match.player2}
                        </div>
                        
                        {matchProbabilities[match.id]?.calculated ? (
                          <div style={{ 
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '5px'
                          }}>
                            <WinGauge 
                              value={parseFloat(matchProbabilities[match.id].player2.probability)} 
                              size={60}
                              primary={parseFloat(matchProbabilities[match.id].player2.probability) > 50 ? '#5cb85c' : '#c74a4a'}
                            />
                            {matchProbabilities[match.id].isSimpleCalc && (
                              <div style={{ fontSize: '9px', color: '#f0ad4e', textAlign: 'left' }}>
                                calcul simple
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ 
                            marginTop: '5px', 
                            fontSize: '11px', 
                            color: '#d9534f', 
                            fontStyle: 'italic',
                            textAlign: 'left'
                          }}>
                            Joueur non trouvé
                          </div>
                        )}
                      </div>
                    </div>

                    
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <button 
                        onClick={() => handleAnalyzeClick(match)}
                        disabled={!isMatchAnalyzable(match) || analyzingMatch === match.id}
                        className={analyzingMatch === match.id ? "analyzing-button" : ""}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '4px',
                          backgroundColor: !isMatchAnalyzable(match) ? '#ccc' : '#3498db',
                          color: 'white',
                          border: 'none',
                          cursor: !isMatchAnalyzable(match) || analyzingMatch === match.id ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {analyzingMatch === match.id ? (
                          <>
                            <span className="loading-text">Analyse en cours...</span>
                            <span className="loading-dots"></span>
                          </>
                        ) : "Analyser ce match"}
                      </button>
                      
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#777',
                        fontStyle: 'italic'
                      }}>
                        Mis à jour le {new Date(match.scraped_date).toLocaleDateString('fr-FR')} à {match.scraped_time ? match.scraped_time.substring(0, 5) : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
      
      <style jsx="true">{`
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        
        @keyframes loadingDots {
          0% { content: "."; }
          33% { content: ".."; }
          66% { content: "..."; }
          100% { content: ""; }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .analyzing-button {
          animation: pulse 1.5s infinite;
          background-color: #3498db !important;
        }
        
        .loading-dots::after {
          content: "";
          animation: loadingDots 1.5s infinite;
        }
        
        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(52, 152, 219, 0.3);
          border-radius: 50%;
          border-top-color: #3498db;
          animation: spin 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default UpcomingMatches; 